import React, { useState, useEffect } from 'react';
import demographicsService, { DemographicsSummary } from '../../../services/demographicsService';
import {
  ChartBarIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  LanguageIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const DemographicsViewerPage: React.FC = () => {
  const [demographics, setDemographics] = useState<DemographicsSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDemographics();
  }, []);

  const loadDemographics = async () => {
    try {
      setLoading(true);
      const data = await demographicsService.getSummary();
      setDemographics(data);
    } catch (error) {
      console.error('Failed to load demographics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'json' | 'xlsx') => {
    try {
      const blob = await demographicsService.export(format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `demographics-${Date.now()}.${format.toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to export demographics:', error);
      alert('Failed to export demographics.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ChartBarIcon className="h-10 w-10 text-blue-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Demographics & Analytics</h1>
                <p className="text-gray-600 mt-1">Understand your patron base</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadDemographics}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <div className="relative inline-block text-left">
                <button
                  onClick={() => handleExport('csv')}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
            <span className="ml-2 text-gray-600">Loading demographics...</span>
          </div>
        ) : !demographics ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No data available</h3>
            <p className="mt-1 text-sm text-gray-500">Demographics will appear once you have patrons</p>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <UserGroupIcon className="h-8 w-8 text-blue-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Patrons</p>
                    <p className="text-2xl font-bold text-gray-900">{demographics.totalPatrons}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <GlobeAltIcon className="h-8 w-8 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Countries</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {demographics.countries ? demographics.countries.length : 0}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <LanguageIcon className="h-8 w-8 text-purple-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Languages</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {demographics.languages ? demographics.languages.length : 0}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <DevicePhoneMobileIcon className="h-8 w-8 text-orange-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Mobile Users</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {demographics.devices?.find((d) => d.device === 'mobile')?.count || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Geographic Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <GlobeAltIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Top Countries
                </h3>
                <div className="space-y-3">
                  {demographics.countries?.slice(0, 10).map((item, idx) => (
                    <div key={item.country} className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <span className="text-sm font-medium text-gray-900 w-8">{idx + 1}.</span>
                        <span className="text-sm text-gray-700">{item.country}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-900 w-16 text-right">
                          {item.count} ({item.percentage.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <LanguageIcon className="h-5 w-5 mr-2 text-purple-600" />
                  Top Languages
                </h3>
                <div className="space-y-3">
                  {demographics.languages?.slice(0, 10).map((item, idx) => (
                    <div key={item.language} className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <span className="text-sm font-medium text-gray-900 w-8">{idx + 1}.</span>
                        <span className="text-sm text-gray-700">{item.language}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-purple-600 h-2 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-900 w-16 text-right">
                          {item.count} ({item.percentage.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Devices & Sources */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DevicePhoneMobileIcon className="h-5 w-5 mr-2 text-orange-600" />
                  Device Breakdown
                </h3>
                <div className="space-y-3">
                  {demographics.devices?.map((item) => (
                    <div key={item.device} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 capitalize">{item.device}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-40 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-orange-600 h-2 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-900 w-16 text-right">
                          {item.count} ({item.percentage.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Signup Sources</h3>
                <div className="space-y-3">
                  {demographics.signupSources?.slice(0, 10).map((item) => (
                    <div key={item.source} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{item.source}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-40 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-900 w-16 text-right">
                          {item.count} ({item.percentage.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Age Groups */}
            {demographics.ageGroups && demographics.ageGroups.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Age Distribution</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {demographics.ageGroups.map((item) => (
                    <div key={item.ageGroup} className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{item.count}</p>
                      <p className="text-sm text-gray-600 mt-1">{item.ageGroup}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {item.percentage.toFixed(1)}%
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DemographicsViewerPage;
